"use client";

import Navbar from "@/components/Navbar";
import BottomCta from "@/components/BottomCta";
import Link from "next/link";
import { motion } from "framer-motion";
import { Building2, TrendingDown, Zap, MapPin, Layers, ShoppingCart, Check } from "lucide-react";

export default function ComplianceServicesPage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">
      <Navbar />

      {/* ── HERO SECTION ── */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">
          
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              Compliance & Regulatory Support
            </span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            Compliance Services <br />
            <span className="font-serif font-normal text-[#B8893A]">for UAE Businesses</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            We help businesses stay aligned with UAE regulations through structured and reliable compliance services, ensuring smooth operations without penalties, delays, or legal complications.
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <a href="https://wa.me/971507987470" target="_blank" rel="noopener noreferrer" className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide">
              Talk To An Expert
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── STATS SECTION ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-16 xl:py-20 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {[
            { value: "All UAE Businesses", label: "Coverage" },
            { value: "Year Round", label: "Support" },
            { value: "Compliance & Records", label: "Focus" },
          ].map((s, i) => (
            <div key={i} className="flex flex-col justify-center">
              <div className="text-2xl sm:text-3xl xl:text-4xl font-black text-[#B8893A] mb-2">{s.value}</div>
              <div className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-widest">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHY COMPLIANCE MATTERS ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-[#FAFAFA]">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Why Compliance Matters</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Proper compliance keeps your business secure, professional, and ready for growth.</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {[
              { icon: Building2, title: "Meet UAE Regulations", text: "Stay compliant with UAE legal and regulatory requirements." },
              { icon: TrendingDown, title: "Avoid Penalties", text: "Reduce the risk of fines, delays, and business disruptions." },
              { icon: Layers, title: "Professional Records", text: "Maintain accurate and organized company documentation." },
              { icon: MapPin, title: "Build Trust", text: "Strengthen credibility with banks, authorities, and partners." },
              { icon: Zap, title: "Smooth Operations", text: "Keep your business running efficiently without compliance issues." },
              { icon: ShoppingCart, title: "Ongoing Monitoring", text: "Never miss important deadlines or regulatory requirements." },
            ].map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div key={i} className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 shadow-md hover:shadow-xl flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-500 font-light text-sm leading-relaxed">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── OUR APPROACH ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Our Approach</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Simple, structured, and fully managed compliance process</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
            {[
              { num: "01", title: "Review & Assessment", text: "We evaluate your current compliance status and identify potential gaps or risks." },
              { num: "02", title: "Planning", text: "We create a clear roadmap with practical actions and timelines for compliance." },
              { num: "03", title: "Execution", text: "Our team handles filings, documentation, registrations, and regulatory requirements." },
              { num: "04", title: "Support", text: "Continuous monitoring, updates, and guidance to maintain compliance standards." },
            ].map((step, i) => (
              <div key={i} className="relative group">
                <div className="text-6xl xl:text-7xl font-black text-gray-100 group-hover:text-[#B8893A]/10 transition-colors duration-300 mb-4">{step.num}</div>
                <h3 className="text-lg font-bold mb-2 tracking-tight">{step.title}</h3>
                <p className="text-gray-500 font-light text-sm leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPLIANCE SERVICES (ACTIVITIES) ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-[#FAFAFA] border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Compliance Services</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Complete regulatory and compliance support</h2>
            <p className="text-gray-500 font-light leading-relaxed">Designed to keep your business aligned with regulatory requirements.</p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              "Trade License Renewal",
              "Company Document Management",
              "Regulatory Compliance Support",
              "ESR Guidance",
              "UBO Filing Support",
              "Corporate Record Maintenance",
              "Compliance Monitoring"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-gray-100 bg-white rounded-xl">
                <Check className="w-4 h-4 text-[#B8893A] shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCta
        title="Stay compliant. Stay confident."
        subtitle="We ensure your business never misses a regulatory requirement."
      />
    </main>
  );
}